import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { normalizeRubbleSource } from '../../../../ft3d/engine/rubble-source.js';
const schema = 'ft.inventory-transform-3d.v1';
const arrayLike = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key) ? value[key] : undefined;
function validUnicode(text: string): boolean {
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff) {
            const next = text.charCodeAt(++i);
            if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
        } else if (code >= 0xdc00 && code <= 0xdfff) return false;
    }
    return true;
}
/** Validate the complete inner JSON text, including overwritten duplicate keys:
 * PHP json_decode(..., depth: 8) rejects excess depth and malformed UTF-16 escapes.
 */
export function decode(text: string): unknown {
    try {
        const result: unknown = JSON.parse(text);
        let depth = 0, quoted = false, escaped = false, start = 0;
        for (let i = 0; i < text.length; i++) {
            const character = text[i];
            if (quoted) {
                if (escaped) escaped = false;
                else if (character === '\\') escaped = true;
                else if (character === '"') {
                    quoted = false;
                    if (!validUnicode(JSON.parse(text.slice(start, i + 1)) as string)) return null;
                }
            } else if (character === '"') { quoted = true; start = i; }
            else if (character === '{' || character === '[') { if (++depth >= 8) return null; }
            else if (character === '}' || character === ']') depth--;
        }
        return result;
    } catch { return null; }
}
/** Complete ft_normalize_inventory_transform_3d; no renderer, geometry or DB access. */
export function normalizeInventoryTransform(input: unknown): Record<string, unknown> | null {
    const value = typeof input === 'string' ? decode(input) : input;
    if (!arrayLike(value) || (own(value, 'schema') ?? schema) !== schema) return null;
    const out: Record<string, unknown> = { schema };
    if (own(value, 'renderMode') === 'standee') out.renderMode = 'standee';
    if (own(value, 'scaleMode') === 'absolute') out.scaleMode = 'absolute';
    if (own(value, 'mounted') === true) out.mounted = true;
    const rotation = own(value, 'rotationDegrees');
    if (rotation != null && rotation !== '' && isPhpNumeric(rotation)) {
        let angle = phpJsonFloat(rotation) % 360;
        if (angle < 0) angle += 360;
        angle = roundPhpDecimal(angle, 4);
        if (Math.abs(angle) >= 0.0001 && Math.abs(angle - 360) >= 0.0001) out.rotationDegrees = angle;
    }
    const scale = own(value, 'scale');
    if (scale != null && scale !== '' && isPhpNumeric(scale)) {
        const size = roundPhpDecimal(phpJsonFloat(scale), 4);
        if (!Number.isFinite(size) || size < 0.05 || size > 24) return null;
        if (Math.abs(size - 1) >= 0.0001) out.scale = size;
    }
    const rubbleSource = normalizeRubbleSource(own(value, 'rubbleSource'));
    if (Object.prototype.hasOwnProperty.call(value, 'scaleXYZ')) {
        const axes = own(value, 'scaleXYZ');
        if (!Array.isArray(axes) || axes.length !== 3 || !axes.every(axis => isPhpNumeric(axis)
            && Number.isFinite(phpJsonFloat(axis)) && phpJsonFloat(axis) >= 0.05 && phpJsonFloat(axis) <= 24)) return null;
        const normalized = axes.map(axis => roundPhpDecimal(phpJsonFloat(axis), 4));
        if (normalized.some(axis => axis !== 1)) out.scaleXYZ = normalized;
    }
    if (rubbleSource) out.rubbleSource = rubbleSource;
    return Object.keys(out).length === 1 ? null : out;
}

export function readInventoryTransform(row: ItemFieldRow | null): Record<string, unknown> | null {
    return normalizeInventoryTransform(readInventoryField(row, 'transform_3d'));
}
export function readInventoryEffectiveMeshScale(row: ItemFieldRow, sizeScale: number): number {
    const transform = readInventoryTransform(row), hand = phpJsonFloat(transform?.scale ?? 1);
    return Math.max(0.05, Math.min(24, transform?.scaleMode === 'absolute' ? hand : hand * sizeScale));
}
